<template>
  <section class="pm-hero" :class="heroClass">
    <div class="pm-hero__bg" :style="bgStyle" aria-hidden="true" />
    <div class="pm-hero__overlay" aria-hidden="true" />
    <div v-if="variant === 'mesh'" class="pm-hero__mesh-deco" aria-hidden="true" />
    <div class="pm-hero__inner">
      <el-breadcrumb v-if="showCrumb" separator="/" class="pm-hero__crumb">
        <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item v-for="(c, i) in crumbs" :key="i">{{ c }}</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="pm-hero__row">
        <div class="pm-hero__copy">
          <h1 class="pm-hero__title">{{ title }}</h1>
          <p v-if="subtitle" class="pm-hero__sub">{{ subtitle }}</p>
          <div v-if="$slots.stats" class="pm-hero__stats">
            <slot name="stats" />
          </div>
          <div v-if="$slots.actions" class="pm-hero__actions">
            <slot name="actions" />
          </div>
        </div>
        <div v-if="artSrc" class="pm-hero__art">
          <img :src="artSrc" alt="" @error="onArtError" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "PortalMarketingHero",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    /** light | dark | mesh（纯 CSS 渐变，推荐门户内页） */
    variant: { type: String, default: "light" },
    /** mesh 模式下是否显示右侧装饰插画区 */
    showArt: { type: Boolean, default: true },
    bgImage: { type: String, default: "" },
    bgFallback: { type: String, default: "" },
    artImage: { type: String, default: "" },
    artFallback: { type: String, default: "" },
    crumbs: { type: Array, default: function () { return []; } },
    showCrumb: { type: Boolean, default: true },
  },
  data() {
    return {
      bgFailed: false,
      artFailed: false,
    };
  },
  computed: {
    heroClass() {
      return ["pm-hero--" + this.variant];
    },
    bgStyle() {
      if (this.variant === "mesh") return {};
      const url = this.bgFailed && this.bgFallback ? this.bgFallback : this.bgImage;
      if (!url) return {};
      if (this.variant === "light") {
        return { "--learn-path-hero-bg-image": "url(" + url + ")" };
      }
      return { backgroundImage: "url(" + url + ")" };
    },
    artSrc() {
      if (!this.showArt) return "";
      if (this.artFailed && this.artFallback) return this.artFallback;
      return this.artImage;
    },
  },
  methods: {
    onArtError() {
      this.artFailed = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/portal-marketing.scss";
@import "@/styles/learn-path-hero.scss";
@import "@/styles/portal-page-hero.scss";

.pm-hero {
  position: relative;
  overflow: hidden;
  padding: clamp(28px, 4vw, 44px) clamp(16px, 3vw, 48px) clamp(36px, 5vw, 52px);
}

.pm-hero__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center right;
  background-repeat: no-repeat;
}

.pm-hero__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.pm-hero--light .pm-hero__bg {
  @include learn-path-hero-bg-layer;
}

.pm-hero--light .pm-hero__overlay {
  @include learn-path-hero-overlay-layer;
}

.pm-hero--dark .pm-hero__bg {
  background-color: #0b1220;
}

.pm-hero--dark .pm-hero__overlay {
  background: linear-gradient(
    105deg,
    rgba(6, 12, 28, 0.92) 0%,
    rgba(8, 18, 42, 0.65) 45%,
    rgba(4, 14, 36, 0.25) 100%
  );
}

.pm-hero--mesh {
  @include portal-hero-deco-blob;
}

.pm-hero--mesh .pm-hero__bg {
  @include portal-hero-mesh-blue;
}

.pm-hero--mesh .pm-hero__overlay {
  background: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0.35) 0%,
    rgba(248, 250, 252, 0.15) 55%,
    transparent 100%
  );
}

.pm-hero__mesh-deco {
  position: absolute;
  right: clamp(24px, 8vw, 120px);
  top: 50%;
  transform: translateY(-50%);
  width: min(36%, 320px);
  height: min(220px, 40vw);
  z-index: 1;
  display: none;
  pointer-events: none;

  @media (min-width: 900px) {
    display: block;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 24px 48px rgba(22, 119, 255, 0.12);
    backdrop-filter: blur(10px);
  }

  &::after {
    content: "";
    position: absolute;
    width: 56%;
    height: 56%;
    left: 22%;
    top: 22%;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(22, 119, 255, 0.15), rgba(64, 169, 255, 0.08));
    border: 1px dashed rgba(22, 119, 255, 0.25);
  }
}

.pm-hero__inner {
  position: relative;
  z-index: 2;
  @include portal-shell-wide;
}

.pm-hero__crumb {
  @include learn-path-hero-crumb;
  margin-bottom: 14px;
}

.pm-hero--dark .pm-hero__crumb {
  ::v-deep .el-breadcrumb__inner,
  ::v-deep .el-breadcrumb__separator {
    color: rgba(255, 255, 255, 0.55);
  }
  ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner {
    color: rgba(255, 255, 255, 0.85);
  }
}

.pm-hero__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px 32px;
}

.pm-hero__copy {
  flex: 1;
  min-width: 0;
}

.pm-hero__title {
  @include learn-path-hero-title;
}

.pm-hero--dark .pm-hero__title {
  color: #fff;
}

.pm-hero__sub {
  @include learn-path-hero-sub;
}

.pm-hero--dark .pm-hero__sub {
  color: rgba(255, 255, 255, 0.75);
}

.pm-hero__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.pm-hero__actions {
  margin-top: 16px;
}

.pm-hero__art {
  flex-shrink: 0;
  width: min(42%, 360px);
  display: none;

  @media (min-width: 900px) {
    display: block;
  }

  img {
    width: 100%;
    height: auto;
    max-height: 220px;
    object-fit: contain;
    filter: drop-shadow(0 12px 28px rgba(22, 119, 255, 0.15));
  }
}
</style>
